/**
 * Voice Onboarding Modal (FEAT-0008).
 *
 * Standalone modal that hosts the tenant brand-voice questionnaire.
 * Opened from the "Setup Brand Voice for AI" button when no voice
 * profile exists for the tenant. Once saved, the parent flips the
 * button label to "Generate Templates (AI)" and the Copy Suggester
 * is unblocked.
 *
 * Rendered as a 3-step wizard (Business · Voice · Examples) driven by
 * the schema's sections so a long questionnaire never feels lengthy.
 * Field widgets: dropdown (industry), typeable country multi-select,
 * checkbox groups (who you message / primary goals), and chip pickers
 * for every voice attribute (tone + formality/warmth/urgency/…).
 *
 * Local-only.
 */
import React, { useEffect, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  Box, Button, Flex, HStack, VStack, Text, Tag, TagLabel, Spinner, Wrap, WrapItem,
  Checkbox, CheckboxGroup, FormControl, FormLabel, Input, Select, useToast,
  Alert, AlertIcon, AlertDescription,
} from "@chakra-ui/react";
import ReactSelect from "react-select";
import { LuSparkles } from "react-icons/lu";
import { COUNTRIES } from "./countries";
import {
  getVoiceProfile, saveVoiceProfile, getVoiceProfileSchema, AGENTS_CONSOLE_URL,
} from "./aiAgentsClient";

// "faith_led" → "Faith Led". Every word capitalised, underscores gone.
const titleCase = (s) =>
  String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const isEmptyVal = (v) =>
  v == null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === "");

export default function VoiceOnboardingModal({ isOpen, onClose, onSaved }) {
  const [phase,    setPhase]    = useState("loading"); // loading | ready | saving | done | error
  const [schema,   setSchema]   = useState(null);
  const [existing, setExisting] = useState(null);   // null = new tenant; obj = re-edit
  const [data,     setData]     = useState({});
  const [step,     setStep]     = useState(0);
  const [error,    setError]    = useState(null);
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setPhase("loading"); setError(null); setStep(0);
      try {
        const [s, p] = await Promise.all([getVoiceProfileSchema(), getVoiceProfile()]);
        if (cancelled) return;
        const sch = s.schema;
        setSchema(sch);
        setExisting(p?.profile || null);
        setData(p?.profile ? prefillFromProfile(p.profile, sch) : initialFormState(sch));
        setPhase("ready");
      } catch (e) {
        setError(`Agents console not reachable at ${AGENTS_CONSOLE_URL}. ${e.message}`);
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const submit = async () => {
    setPhase("saving"); setError(null);
    try {
      const r = await saveVoiceProfile(data);
      if (r.redactions_applied) {
        toast({
          title: "PII removed from your input",
          description: "We stripped email/phone/IP-looking values before saving.",
          status: "warning",
          duration: 4000,
        });
      } else {
        toast({ title: "Brand voice saved", status: "success", duration: 1500 });
      }
      setPhase("done");
      if (onSaved) onSaved(r.profile);
      onClose();
    } catch (e) {
      setError(e.message);
      setPhase("ready");
    }
  };

  const handleClose = () => {
    if (phase === "saving") return;
    onClose();
  };

  const sections    = schema?.sections || [];
  const stepLabels  = ["Business", "Voice", "Examples"];
  const current     = sections[step];
  const isLast      = step === sections.length - 1;
  const setField    = (sectionKey, fieldKey, v) =>
    setData((prev) => ({ ...prev, [sectionKey]: { ...(prev[sectionKey] || {}), [fieldKey]: v } }));

  // Required-field gate for the current step only.
  const missing = (current?.fields || []).filter(
    (f) => f.required && isEmptyVal(data[current.key]?.[f.key]),
  );
  const stepValid = missing.length === 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" scrollBehavior="inside"
           closeOnOverlayClick={phase !== "saving"}>
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader>
          <HStack spacing={3} align="flex-start">
            <Box w={9} h={9} borderRadius="lg" bg="purple.50"
                 display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
              <LuSparkles color="#8B5CF6" size={18} />
            </Box>
            <Box>
              <Text fontSize="lg" fontWeight="600">
                {existing ? "Edit Brand Voice" : "Setup Brand Voice for AI"}
              </Text>
              <Text fontSize="xs" color="gray.500" mt={0.5} fontWeight="400">
                {existing
                  ? "Update your brand voice profile. Used by all AI template generation."
                  : "Help the AI write copy that sounds like your brand. Saved once per tenant."}
              </Text>
            </Box>
            <HStack spacing={1} ml="auto" pr={6}>
              {phase === "loading" && <Tag size="sm"><TagLabel>loading…</TagLabel></Tag>}
              {phase === "saving"  && <Tag size="sm" colorScheme="purple"><TagLabel>saving</TagLabel></Tag>}
              {existing && phase === "ready" && (
                <Tag size="sm" colorScheme="green"><TagLabel>profile on file</TagLabel></Tag>
              )}
            </HStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton isDisabled={phase === "saving"} />
        <ModalBody pb={6}>
          {phase === "loading" && (
            <Flex direction="column" align="center" py={10} gap={2}>
              <Spinner size="md" color="purple.500" />
              <Text fontSize="sm" color="gray.500">Loading questionnaire…</Text>
            </Flex>
          )}
          {error && (
            <Alert status="error" borderRadius="md" mb={4}>
              <AlertIcon />
              <AlertDescription fontSize="sm">{error}</AlertDescription>
            </Alert>
          )}
          {phase !== "loading" && schema && current && (
            <VStack align="stretch" spacing={4}>
              <Stepper labels={stepLabels} step={step} onJump={setStep} />

              {step === 0 && (
                <Box bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="lg" p={3}>
                  <Text fontSize="xs" color="purple.800">{schema.intro}</Text>
                </Box>
              )}

              <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={4}>
                <Text fontWeight="600" fontSize="sm" color="gray.700" mb={3}>
                  {titleCase(stepLabels[step])}
                </Text>
                <VStack spacing={4} align="stretch">
                  {current.fields.map((field) => (
                    <QuestionnaireField key={field.key} field={field}
                      value={data[current.key]?.[field.key]}
                      onChange={(v) => setField(current.key, field.key, v)} />
                  ))}
                </VStack>
              </Box>

              {!stepValid && (
                <Text fontSize="xs" color="red.500">
                  Complete the required field{missing.length > 1 ? "s" : ""} marked&nbsp;*&nbsp;to continue.
                </Text>
              )}

              <Flex justify="space-between" align="center" gap={2} pt={1}>
                <Button variant="ghost" size="sm"
                        onClick={() => (step === 0 ? handleClose() : setStep(step - 1))}
                        isDisabled={phase === "saving"}>
                  {step === 0 ? "Cancel" : "Back"}
                </Button>
                <HStack spacing={3}>
                  <Text fontSize="xs" color="gray.400">Step {step + 1} of {sections.length}</Text>
                  {isLast ? (
                    <Button colorScheme="purple" size="md" onClick={submit}
                            isDisabled={!stepValid}
                            isLoading={phase === "saving"} loadingText="Saving">
                      {existing ? "Update Brand Voice" : "Save Brand Voice"}
                    </Button>
                  ) : (
                    <Button colorScheme="purple" size="md"
                            onClick={() => setStep(step + 1)} isDisabled={!stepValid}>
                      Next
                    </Button>
                  )}
                </HStack>
              </Flex>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

/* ───────────────────────── Stepper header ───────────────────────── */
function Stepper({ labels, step, onJump }) {
  return (
    <HStack spacing={0} align="center" px={1}>
      {labels.map((label, i) => {
        const done = i < step;
        const active = i === step;
        return (
          <React.Fragment key={label}>
            <HStack spacing={2} cursor={i <= step ? "pointer" : "default"}
                    onClick={() => (i <= step ? onJump(i) : null)}>
              <Flex w={7} h={7} borderRadius="full" align="center" justify="center"
                    fontSize="xs" fontWeight="700"
                    bg={active ? "purple.500" : done ? "purple.100" : "gray.100"}
                    color={active ? "white" : done ? "purple.600" : "gray.500"}
                    border="1px solid"
                    borderColor={active ? "purple.500" : done ? "purple.200" : "gray.200"}>
                {done ? "✓" : i + 1}
              </Flex>
              <Text fontSize="sm" fontWeight={active ? "600" : "500"}
                    color={active ? "purple.700" : done ? "gray.600" : "gray.400"}>
                {label}
              </Text>
            </HStack>
            {i < labels.length - 1 && (
              <Box flex="1" h="1px" mx={3} bg={i < step ? "purple.200" : "gray.200"} />
            )}
          </React.Fragment>
        );
      })}
    </HStack>
  );
}

function initialFormState(schema) {
  const out = {};
  for (const section of schema.sections || []) {
    out[section.key] = {};
    for (const field of section.fields || []) {
      out[section.key][field.key] =
        ["multi_select", "checkbox", "country_multi"].includes(field.type) ? []
        : field.type === "csv" ? ""
        : field.repeat_max     ? [""]
        : "";
    }
  }
  return out;
}

/**
 * Re-fill the form from a previously-saved profile so the operator can
 * edit + re-save. Multi-value fields (country/audience/goals/tone) come
 * back as arrays; legacy string profiles are split to arrays. csv fields
 * are re-joined to a comma string for the input.
 */
function prefillFromProfile(profile, schema) {
  const seed = initialFormState(schema);
  const arrayTypes = ["multi_select", "checkbox", "country_multi"];
  const merge = (sectionKey, srcObj) => {
    if (!srcObj) return;
    for (const field of (schema.sections.find((s) => s.key === sectionKey)?.fields || [])) {
      const v = srcObj[field.key];
      if (v == null) continue;
      if (arrayTypes.includes(field.type)) {
        seed[sectionKey][field.key] = Array.isArray(v)
          ? v
          : String(v).split(",").map((x) => x.trim()).filter(Boolean);
      } else if (field.type === "csv" && Array.isArray(v)) {
        seed[sectionKey][field.key] = v.join(", ");
      } else {
        seed[sectionKey][field.key] = v;
      }
    }
  };
  merge("business_context", profile.business_context);
  merge("voice",            profile.voice);
  merge("examples",         profile.examples);
  return seed;
}

function FieldHelp({ text }) {
  if (!text) return null;
  return <Text fontSize="11px" color="gray.500" mt={1.5}>{text}</Text>;
}

/* react-select styling tuned to sit inside the Chakra modal. */
const rsStyles = {
  control: (base) => ({ ...base, minHeight: 38, borderColor: "#E2E8F0", fontSize: 14 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  multiValue: (base) => ({ ...base, backgroundColor: "#F3E8FF" }),
  multiValueLabel: (base) => ({ ...base, color: "#6B21A8" }),
};

function QuestionnaireField({ field, value, onChange }) {
  const { label, type, widget, required, options, placeholder, maxLength, repeat_max, help } = field;
  const labelText = `${label}${required ? " *" : ""}`;
  const Lbl = (
    <FormLabel fontSize="xs" mb={1.5} color="gray.600" fontWeight="600">{labelText}</FormLabel>
  );

  // Typeable, multi-country picker.
  if (type === "country_multi") {
    const selected = (Array.isArray(value) ? value : []).map((c) => ({ value: c, label: c }));
    return (
      <FormControl isRequired={required}>
        {Lbl}
        <ReactSelect
          isMulti
          options={COUNTRIES.map((c) => ({ value: c, label: c }))}
          value={selected}
          onChange={(sel) => onChange((sel || []).map((o) => o.value))}
          placeholder={placeholder || "Type to search countries…"}
          classNamePrefix="brandvoice-country"
          menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
          styles={rsStyles}
          closeMenuOnSelect={false}
        />
        <FieldHelp text={help} />
      </FormControl>
    );
  }

  // Checkbox group — "who you message", "primary goals".
  if (type === "checkbox") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <FormControl isRequired={required}>
        {Lbl}
        <CheckboxGroup value={selected} onChange={(vals) => onChange(vals)} colorScheme="purple">
          <Wrap spacing={3} shouldWrapChildren>
            {options.map((o) => (
              <WrapItem key={o}>
                <Checkbox value={o} size="md">
                  <Text fontSize="sm">{titleCase(o)}</Text>
                </Checkbox>
              </WrapItem>
            ))}
          </Wrap>
        </CheckboxGroup>
        <FieldHelp text={help} />
      </FormControl>
    );
  }

  // Chip pickers — multi for tone, single for every other voice attribute.
  if (type === "multi_select" || widget === "chips") {
    const isMulti = type === "multi_select";
    const selected = isMulti ? (Array.isArray(value) ? value : []) : value;
    const isOn = (o) => (isMulti ? selected.includes(o) : selected === o);
    const toggle = (o) => {
      if (isMulti) onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);
      else onChange(selected === o ? "" : o);
    };
    return (
      <FormControl isRequired={required}>
        {Lbl}
        <Wrap spacing={2.5}>
          {options.map((o) => (
            <WrapItem key={o}>
              <Tag size="lg" borderRadius="full" px={4} py={2} cursor="pointer" fontSize="sm"
                   variant={isOn(o) ? "solid" : "outline"}
                   colorScheme={isOn(o) ? "purple" : "gray"}
                   _hover={{ borderColor: "purple.300" }}
                   onClick={() => toggle(o)}>
                <TagLabel>{titleCase(o)}</TagLabel>
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
        <FieldHelp text={help} />
      </FormControl>
    );
  }

  // Single-choice dropdown — industry.
  if (type === "select") {
    return (
      <FormControl isRequired={required}>
        {Lbl}
        <Select size="md" value={value || ""} onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder || "Select…"}>
          {options.map((o) => <option key={o} value={o}>{titleCase(o)}</option>)}
        </Select>
        <FieldHelp text={help} />
      </FormControl>
    );
  }

  if (type === "csv") {
    return (
      <FormControl isRequired={required}>
        {Lbl}
        <Input size="md" value={value || ""} maxLength={maxLength}
               onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        <FieldHelp text={help || "Comma-separated."} />
      </FormControl>
    );
  }

  if (type === "textarea" && repeat_max) {
    const arr = Array.isArray(value) ? value : [value || ""];
    const update = (idx, v) => { const next = [...arr]; next[idx] = v; onChange(next); };
    const add = () => onChange([...arr, ""]);
    return (
      <FormControl>
        {Lbl}
        <VStack spacing={2} align="stretch">
          {arr.slice(0, repeat_max).map((entry, idx) => (
            <textarea key={idx} value={entry || ""} maxLength={maxLength}
              onChange={(e) => update(idx, e.target.value)}
              placeholder={placeholder || "Paste an example message"}
              style={{ minHeight: 60, padding: 8, fontSize: 13, border: "1px solid #e2e8f0",
                       borderRadius: 6, fontFamily: "inherit" }} />
          ))}
          {arr.length < repeat_max && (
            <Button size="xs" variant="ghost" onClick={add} alignSelf="flex-start">+ another</Button>
          )}
        </VStack>
        <FieldHelp text={help} />
      </FormControl>
    );
  }

  if (type === "textarea") {
    return (
      <FormControl isRequired={required}>
        {Lbl}
        <textarea value={value || ""} maxLength={maxLength}
          onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          style={{ minHeight: 70, padding: 8, fontSize: 13, width: "100%",
                   border: "1px solid #e2e8f0", borderRadius: 6, fontFamily: "inherit" }} />
        <FieldHelp text={help} />
      </FormControl>
    );
  }

  return (
    <FormControl isRequired={required}>
      {Lbl}
      <Input size="md" value={value || ""} maxLength={maxLength}
             onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      <FieldHelp text={help} />
    </FormControl>
  );
}
